import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRomeoJobs, getRomeoStats, startScrapeAsync, getScrapeTaskStatus, jobAction, processJob, getJobDetails, triggerJobMatching, getMatchTaskStatus } from '../services/api';
import JobDetailsModal from './JobDetailsModal';
import './Romeo.css';
import '../contexts/CustomSearchContext.css';

function Romeo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    total_jobs: 0,
    ready: 0,
    matched: 0,
    processing: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [practiceAreaFilter, setPracticeAreaFilter] = useState('all');
  const [workTypeFilter, setWorkTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [lastIngestedAt, setLastIngestedAt] = useState(() => {
    try {
      return localStorage.getItem('romeo_last_ingested_at') || null;
    } catch {
      return null;
    }
  });
  const [matchingJobIds, setMatchingJobIds] = useState([]);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [resettingJobId, setResettingJobId] = useState(null);
  const [zeroMatchesModal, setZeroMatchesModal] = useState(null); // { jobTitle, errorMessage }
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const allJobsCheckboxRef = useRef(null);

  // Sync notification popup
  const [syncNotification, setSyncNotification] = useState(null); // { newJobs, updatedJobs }
  const syncPollingRef = useRef(null);
  // Accumulates per-source results during polling; reset on each new sync.
  const pendingSyncResultsRef = useRef({ zip: null, serp: null });
  // Stable ref so startSyncPolling (defined before fetchData) can always call
  // the latest version of fetchData without creating a circular dependency.
  const fetchDataRef = useRef(null);

  const SYNC_TRIGGER_KEY = 'romeo_last_ingested_at';
  const ZIP_TASK_KEY = 'romeo_zip_task_id';
  const SERP_TASK_KEY = 'romeo_serp_task_id';

  // Hard cap: stop polling after 22 minutes so the Syncing button can't stay
  // active indefinitely. The backend task itself is capped at 15/20 min.
  const SYNC_POLL_TIMEOUT_MS = 22 * 60 * 1000;
  const syncTimeoutRef = useRef(null);

  const stopSyncPolling = useCallback((timedOut = false) => {
    if (syncPollingRef.current) { clearInterval(syncPollingRef.current); syncPollingRef.current = null; }
    if (syncTimeoutRef.current) { clearTimeout(syncTimeoutRef.current); syncTimeoutRef.current = null; }
    try { localStorage.removeItem(ZIP_TASK_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(SERP_TASK_KEY); } catch { /* ignore */ }
    pendingSyncResultsRef.current = { zip: null, serp: null };
    setSyncing(false);
    if (timedOut) {
      setSyncNotification({ newJobs: 0, updatedJobs: 0, timedOut: true });
      if (fetchDataRef.current) fetchDataRef.current();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startSyncPolling = useCallback((zipTaskId, serpTaskId) => {
    if (syncPollingRef.current) clearInterval(syncPollingRef.current);
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

    // Hard timeout: give up after 22 minutes regardless of task state
    syncTimeoutRef.current = setTimeout(() => {
      console.warn('[sync polling] timed out after 22 minutes — stopping');
      stopSyncPolling(true);
    }, SYNC_POLL_TIMEOUT_MS);

    syncPollingRef.current = setInterval(async () => {
      try {
        const results = pendingSyncResultsRef.current;

        // Poll ZipRecruiter task if still pending
        if (zipTaskId && results.zip === null) {
          try {
            const status = await getScrapeTaskStatus(zipTaskId);
            if (status.state === 'SUCCESS') {
              results.zip = { newJobs: status.new_jobs || 0, updatedJobs: status.updated_jobs || 0 };
              try { localStorage.removeItem(ZIP_TASK_KEY); } catch { /* ignore */ }
            } else if (status.state === 'FAILURE') {
              results.zip = { newJobs: 0, updatedJobs: 0 };
              try { localStorage.removeItem(ZIP_TASK_KEY); } catch { /* ignore */ }
            }
          } catch { /* network hiccup — keep polling */ }
        }

        // Poll SerpAPI task if still pending
        if (serpTaskId && results.serp === null) {
          try {
            const status = await getScrapeTaskStatus(serpTaskId);
            if (status.state === 'SUCCESS') {
              results.serp = { newJobs: status.new_jobs || 0, updatedJobs: status.updated_jobs || 0 };
              try { localStorage.removeItem(SERP_TASK_KEY); } catch { /* ignore */ }
            } else if (status.state === 'FAILURE') {
              results.serp = { newJobs: 0, updatedJobs: 0 };
              try { localStorage.removeItem(SERP_TASK_KEY); } catch { /* ignore */ }
            }
          } catch { /* network hiccup — keep polling */ }
        }

        const zipDone = !zipTaskId || results.zip !== null;
        const serpDone = !serpTaskId || results.serp !== null;

        if (zipDone && serpDone) {
          if (syncTimeoutRef.current) { clearTimeout(syncTimeoutRef.current); syncTimeoutRef.current = null; }
          clearInterval(syncPollingRef.current);
          syncPollingRef.current = null;

          const totalNew = (results.zip?.newJobs || 0) + (results.serp?.newJobs || 0);
          const totalUpdated = (results.zip?.updatedJobs || 0) + (results.serp?.updatedJobs || 0);

          setSyncing(false);
          setSyncNotification({ newJobs: totalNew, updatedJobs: totalUpdated });
          pendingSyncResultsRef.current = { zip: null, serp: null };
          if (fetchDataRef.current) fetchDataRef.current();
        }
      } catch (err) {
        console.warn('[sync polling] request failed, will retry:', err.message);
      }
    }, 4000);
  }, [stopSyncPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    try {
      if (hasLoadedOnce) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      const [jobsData, statsData] = await Promise.all([
        getRomeoJobs({
          page: currentPage,
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          practice_area: getPracticeAreaFilterValue(practiceAreaFilter),
          work_type: workTypeFilter !== 'all' ? workTypeFilter : undefined,
        }),
        getRomeoStats(),
      ]);
      
      setJobs(jobsData.results || []);
      setTotalPages(Math.ceil((jobsData.count || 0) / 10));
      setStats(statsData);
      setError(null);
      setHasLoadedOnce(true);
    } catch (err) {
      setError(err.message);
    } finally {
      if (hasLoadedOnce) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [currentPage, searchTerm, statusFilter, practiceAreaFilter, workTypeFilter, hasLoadedOnce]);

  // Keep the ref in sync so the polling interval always calls the latest fetchData.
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Reset to page 1 whenever filters change so we never request a page that doesn't exist
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm, practiceAreaFilter, workTypeFilter]);

  // Clear selections when filters, search, or page change
  useEffect(() => {
    setSelectedJobIds([]);
  }, [currentPage, statusFilter, searchTerm, practiceAreaFilter, workTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // On mount: if a sync was already in-flight before the page was refreshed or
  // navigated away, resume polling so the completion notification still fires.
  useEffect(() => {
    let zipTaskId = null;
    let serpTaskId = null;
    try { zipTaskId = localStorage.getItem(ZIP_TASK_KEY); } catch { /* ignore */ }
    try { serpTaskId = localStorage.getItem(SERP_TASK_KEY); } catch { /* ignore */ }
    if (zipTaskId || serpTaskId) {
      setSyncing(true);
      pendingSyncResultsRef.current = { zip: null, serp: null };
      startSyncPolling(zipTaskId, serpTaskId);
    }
    return () => {
      if (syncPollingRef.current) clearInterval(syncPollingRef.current);
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleSync = async () => {
    try {
      setSyncing(true);

      // Record trigger time immediately so the UI updates right away
      const now = new Date().toISOString();
      setLastIngestedAt(now);
      try { localStorage.setItem(SYNC_TRIGGER_KEY, now); } catch { /* ignore */ }

      pendingSyncResultsRef.current = { zip: null, serp: null };

      // Fire both ingestion tasks in parallel
      const [zipResult, serpResult] = await Promise.allSettled([
        startScrapeAsync({ source: 'ziprecruiter', limit: 300 }),
        startScrapeAsync({ source: 'serpapi', limit: 100, date_posted: 'week' }),
      ]);

      const zipTaskId = zipResult.status === 'fulfilled' ? zipResult.value.task_id : null;
      const serpTaskId = serpResult.status === 'fulfilled' ? serpResult.value.task_id : null;

      if (zipTaskId) try { localStorage.setItem(ZIP_TASK_KEY, zipTaskId); } catch { /* ignore */ }
      if (serpTaskId) try { localStorage.setItem(SERP_TASK_KEY, serpTaskId); } catch { /* ignore */ }

      if (!zipTaskId && !serpTaskId) {
        setSyncing(false);
        setError('Sync failed: could not queue any background tasks');
        return;
      }

      startSyncPolling(zipTaskId, serpTaskId);
    } catch (err) {
      setSyncing(false);
      setError(`Sync failed: ${err.message}`);
    }
  };

  const handleRetryJob = async (jobId, e) => {
    e.stopPropagation();
    setRetryingJobId(jobId);
    setJobs(prev => [...prev.map(j => j.id === jobId ? { ...j, status: 'processing' } : j)]);
    setStats(prev => ({ ...prev, failed: Math.max(0, prev.failed - 1), processing: prev.processing + 1 }));
    try {
      await processJob(jobId);
      const updatedJob = await getJobDetails(jobId);
      setJobs(prev => [...prev.map(j => j.id === jobId ? { ...j, status: updatedJob.status, last_processed_at: updatedJob.last_processed_at } : j)]);
      const statsData = await getRomeoStats();
      setStats(statsData);
    } catch (err) {
      setJobs(prev => [...prev.map(j => j.id === jobId ? { ...j, status: 'failed' } : j)]);
      setStats(prev => ({ ...prev, failed: prev.failed + 1, processing: Math.max(0, prev.processing - 1) }));
      setError(`Retry failed: ${err.message}`);
    } finally {
      setRetryingJobId(null);
    }
  };

  const handleJobAction = async (jobId, action) => {
    try {
      await jobAction(jobId, { action });
      await fetchData(); // Refresh data after action
    } catch (err) {
      setError(`Action failed: ${err.message}`);
    }
  };

  const handleResetJob = async (jobId, e) => {
    e.stopPropagation();
    setResettingJobId(jobId);
    try {
      await jobAction(jobId, { action: 'reset_to_new' });
      await fetchData();
      setError(null);
    } catch (err) {
      setError(`Reset failed: ${err.message}`);
    } finally {
      setResettingJobId(null);
    }
  };

  const normalizePracticeArea = (practiceArea) => {
    if (!practiceArea) return practiceArea;
    
    // Normalize common variations to consistent names
    const normalizationMap = {
      'Ip': 'IP',
      'Patent': 'IP',
      'Intellectual Property': 'IP',
      'Real Estate': 'Real Estate',
      'Real estate': 'Real Estate',
      'Employment': 'Employment',
      'Corporate': 'Corporate',
      'Litigation': 'Litigation',
      'Tax': 'Tax',
      'Healthcare': 'Healthcare',
      'Criminal': 'Criminal',
      'Family': 'Family',
      'Immigration': 'Immigration'
    };
    
    return normalizationMap[practiceArea] || practiceArea;
  };

  const getPracticeAreaFilterValue = (value) => {
    if (!value || value === 'all') return undefined;
    return value;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      new: '#9ca3af',
      processing: '#f59e0b',
      matching: '#8b5cf6',
      shortlisting: '#fbbf24',
      ready: '#10b981',
      matched: '#8b5cf6',
      shortlisted: '#a78bfa',
      explaining: '#8b5cf6',
      outreach_sent: '#10b981',
      intent_received: '#06b6d4',
      failed: '#ef4444',
      closed: '#6b7280',
    };
    return statusColors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      new: 'New',
      processing: 'Processing',
      matching: 'Matching',
      shortlisting: 'Shortlisting',
      ready: 'Ready for Matching',
      matched: 'Ready for Outreach',
      shortlisted: 'Shortlisted',
      explaining: 'Explaining',
      outreach_sent: 'Outreach Sent',
      intent_received: 'Intent Received',
      failed: 'Failed',
      closed: 'Closed',
    };
    return statusLabels[status] || status;
  };

  const getWorkTypeColor = (workType) => {
    const colors = {
      'Remote': '#3b82f6',      // Blue
      'Hybrid': '#8b5cf6',      // Purple
      'In-House': '#6b7280',    // Gray
      'Unknown': '#d1d5db',     // Light gray
    };
    return colors[workType] || colors['Unknown'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleJobClick = (jobId) => {
    setSelectedJobId(jobId);
    setShowModal(true);
  };

  const toggleJobSelection = (jobId, checked) => {
    setSelectedJobIds((prev) => {
      if (checked) {
        if (prev.includes(jobId)) return prev;
        return [...prev, jobId];
      }
      return prev.filter((id) => id !== jobId);
    });
  };

  const handleToggleAllJobs = (checked) => {
    const visibleIds = jobs.map((job) => job.id);
    if (checked) {
      setSelectedJobIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    } else {
      setSelectedJobIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const visibleJobIds = jobs.map((job) => job.id);
  const visibleSelectedCount = selectedJobIds.filter((id) =>
    visibleJobIds.includes(id)
  ).length;
  const allVisibleSelected =
    jobs.length > 0 && visibleSelectedCount === jobs.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < jobs.length;

  useEffect(() => {
    if (allJobsCheckboxRef.current) {
      allJobsCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected, jobs.length]);

  const handleEnableJobIntelligence = async (jobId, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      // Find the job to check its current status
      const job = jobs.find(j => j.id === jobId);
      
      // Only process if job is new (not yet processed)
      if (job && job.status === 'new') {
        // Optimistically update the job status to 'processing' immediately
        // This ensures React sees the state change and triggers a re-render
        setJobs(prevJobs => {
          const updated = prevJobs.map(j =>
            j.id === jobId ? { ...j, status: 'processing' } : j
          );
          // Return new array reference to ensure React detects the change
          return [...updated];
        });
        
        // Update stats optimistically
        setStats(prevStats => ({
          ...prevStats,
          processing: prevStats.processing + 1,
        }));
        
        try {
          // Start processing the job (this is async and returns immediately)
          const processResponse = await processJob(jobId);
          
          // Immediately fetch updated job to get the latest status from database
          // The backend sets status to 'processing' when the task starts
          const updatedJob = await getJobDetails(jobId);
          
          // Update the job in state with only the fields that exist in the list view
          // This ensures we don't pollute the list state with detail-only fields
          setJobs(prevJobs => {
            const updated = prevJobs.map(j => {
              if (j.id === jobId) {
                // Merge only the relevant fields that exist in both serializers
                return {
                  ...j,
                  status: updatedJob.status,
                  last_processed_at: updatedJob.last_processed_at,
                  // Preserve all other existing fields
                };
              }
              return j;
            });
            // Return new array reference to ensure React detects the change
            return [...updated];
          });
          
          // Refresh stats to reflect any changes
          const statsData = await getRomeoStats();
          setStats(statsData);
        } catch (processErr) {
          // Revert optimistic update on error
          setJobs(prevJobs => {
            const updated = prevJobs.map(j =>
              j.id === jobId ? { ...j, status: 'new' } : j
            );
            return [...updated];
          });
          
          // Revert stats
          setStats(prevStats => ({
            ...prevStats,
            processing: Math.max(0, prevStats.processing - 1),
          }));
          
          throw processErr;
        }
      }
    } catch (err) {
      setError(`Failed to enable job intelligence: ${err.message}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJobId(null);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const PAGE_SIZE = 100;
      let page = 1;
      let allJobs = [];
      let hasMore = true;

      while (hasMore) {
        const data = await getRomeoJobs({
          page,
          page_size: PAGE_SIZE,
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          practice_area: getPracticeAreaFilterValue(practiceAreaFilter),
          work_type: workTypeFilter !== 'all' ? workTypeFilter : undefined,
        });
        const results = data.results || [];
        allJobs = allJobs.concat(results);
        hasMore = !!data.next && results.length === PAGE_SIZE;
        page += 1;
      }

      const escapeCsvField = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = ['Job Title', 'Company', 'Practice Area', 'Seniority', 'Firm Location', 'Work Type', 'Post Date', 'Status'];
      const rows = allJobs.map((job) => [
        job.title,
        job.company,
        normalizePracticeArea(job.practice_area),
        job.seniority,
        job.location,
        job.work_type,
        formatDate(job.posted_at || job.created_at),
        getStatusLabel(job.status),
      ].map(escapeCsvField));

      const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `romeo-jobs-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleMatchClick = async (jobId, e) => {
    e.stopPropagation();
    setMatchingJobIds((prev) => prev.includes(jobId) ? prev : [...prev, jobId]);
    try {
      const result = await triggerJobMatching(jobId);

      // Cache hit — navigate immediately
      if (result.cached) {
        navigate(`/matchmaker/${jobId}`);
        return;
      }

      // Task queued — poll until done or failed
      const taskId = result.task_id;
      let lastStatus = null;
      if (taskId) {
        const POLL_INTERVAL = 3000;
        const MAX_POLLS = 60; // 3 min ceiling
        for (let i = 0; i < MAX_POLLS; i++) {
          await new Promise((res) => setTimeout(res, POLL_INTERVAL));
          lastStatus = await getMatchTaskStatus(jobId, taskId);
          if (
            lastStatus.state === 'SUCCESS' ||
            lastStatus.job_status === 'matched' ||
            lastStatus.job_status === 'ready'
          ) {
            break;
          }
          if (lastStatus.state === 'FAILURE' || lastStatus.job_status === 'failed') {
            throw new Error(lastStatus.error || 'Matching failed. Please retry.');
          }
        }
      }

      // 0 matches found — job reverts to 'ready'. Show an explanatory popup instead of
      // navigating to an empty results page.
      if (lastStatus?.job_status === 'ready') {
        const job = jobs.find((j) => j.id === jobId);
        setZeroMatchesModal({
          jobTitle: job?.title || 'this job',
          code: lastStatus.zero_match_code || 'NO_PEARCH_RESULTS',
          detail: lastStatus.zero_match_detail || {},
        });
        setMatchingJobIds((prev) => prev.filter((id) => id !== jobId));
        return;
      }

      navigate(`/matchmaker/${jobId}`);
    } catch (err) {
      setError(`Failed to load matches: ${err.message}`);
      setMatchingJobIds((prev) => prev.filter((id) => id !== jobId));
    }
  };

  return (
    <div className="romeo-page">
      {/* Header */}
      <div className="romeo-header">
        <div className="romeo-header-left">
          <h1 className="romeo-title">
            <span className="romeo-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="#3b82f6"/>
              </svg>
            </span>
            Romeo - Job Intelligence
          </h1>
          <div className="romeo-subtitle-block">
            {lastIngestedAt && (
              <p className="romeo-subtitle">
                Last ingested at{' '}
                <span className="romeo-subtitle-datetime">
                  {formatDateTime(lastIngestedAt)}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="romeo-header-actions">
          <button
            className="btn-secondary"
            onClick={handleExportCSV}
            disabled={isExporting}
            title="Export current view to CSV"
          >
            {isExporting ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="spin">
                <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
              </svg>
            )}
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="btn-primary" onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="spin">
                <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
              </svg>
            )}
            {syncing ? 'Syncing...' : 'Sync Jobs'}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="romeo-stats">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">TOTAL JOBS</div>
            <div className="stat-value">{stats.total_jobs}</div>
            {stats.source_breakdown && (
              <div className="stat-source-breakdown">
                <span className="stat-source-pill stat-source-pill--zip">
                  ZR {stats.source_breakdown.ziprecruiter || 0}
                </span>
                <span className="stat-source-pill stat-source-pill--serp">
                  GJ {stats.source_breakdown.serpapi || 0}
                </span>
              </div>
            )}
          </div>
          <div className="stat-icon stat-icon-blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">READY</div>
            <div className="stat-value">{stats.ready}</div>
          </div>
          <div className="stat-icon stat-icon-green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">MATCHED</div>
            <div className="stat-value">{stats.matched || 0}</div>
          </div>
          <div className="stat-icon stat-icon-purple">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">PROCESSING</div>
            <div className="stat-value">{stats.processing}</div>
          </div>
          <div className="stat-icon stat-icon-yellow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">FAILED</div>
            <div className="stat-value">{stats.failed}</div>
          </div>
          <div className="stat-icon stat-icon-red">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Job Table */}
      <div className="romeo-table-container">
        <div className="romeo-table-header">
          <div className="romeo-table-header-left">
            <input
              type="checkbox"
              className="romeo-checkbox"
              ref={allJobsCheckboxRef}
              checked={allVisibleSelected}
              onChange={(e) => handleToggleAllJobs(e.target.checked)}
            />
            <span className="romeo-table-title">All Jobs ({stats.total_jobs})</span>
          </div>
          <div className="romeo-table-filters">
            <div className="search-box">
              <span className="search-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="processing">Processing</option>
              <option value="ready">Ready for Matching</option>
              <option value="matched">Ready for Outreach</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={practiceAreaFilter}
              onChange={(e) => setPracticeAreaFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Practice Areas</option>
              <option value="Corporate">Corporate</option>
              <option value="IP">IP</option>
              <option value="Litigation">Litigation</option>
              <option value="Tax">Tax</option>
              <option value="Employment">Employment</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Healthcare">Healthcare</option>
            </select>
            <select
              value={workTypeFilter}
              onChange={(e) => setWorkTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Work Types</option>
              <option value="Remote">Remote</option>
              <option value="Hybrid">Hybrid</option>
              <option value="In-House">In-House</option>
            </select>
          </div>
        </div>

        {loading && !hasLoadedOnce ? (
          <div className="loading">Loading jobs...</div>
        ) : error ? (
          <div className="error">Error: {error}</div>
        ) : (
          <>
            {isRefreshing && (
              <div className="table-refresh-indicator">Refreshing jobs...</div>
            )}
            <div className="table-scroll-wrapper">
              <table className="romeo-table">
              <thead>
                <tr>
                  <th></th>
                  <th>JOB TITLE</th>
                  <th>COMPANY</th>
                  <th>SOURCE</th>
                  <th>PRACTICE AREA</th>
                  <th>SENIORITY</th>
                  <th>FIRM LOCATION</th>
                  <th>WORK TYPE</th>
                  <th>POST DATE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-jobs">
                      No jobs found. Try ingesting jobs first.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr 
                      key={job.id}
                      onClick={() => handleJobClick(job.id)}
                      className="job-row"
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="romeo-checkbox"
                          checked={selectedJobIds.includes(job.id)}
                          onChange={(e) =>
                            toggleJobSelection(job.id, e.target.checked)
                          }
                        />
                      </td>
                      <td className="job-title-cell">
                        <div className="job-title-content">
                          <div className="job-title-primary" title={job.title}>{job.title}</div>
                          <div className="job-title-secondary">
                            {job.practice_area && `${normalizePracticeArea(job.practice_area)}`}
                            {job.practice_area && job.company && ' • '}
                            {job.company && `${job.company}`}
                          </div>
                        </div>
                      </td>
                      <td className="company-cell">{job.company}</td>
                      <td className="source-cell">
                        <span className={`source-tag source-tag--${job.source === 'ziprecruiter' ? 'zip' : 'serp'}`}>
                          {job.source === 'ziprecruiter' ? 'ZipRecruiter' : 'Google Jobs'}
                        </span>
                      </td>
                      <td className="practice-area-cell">
                        {job.practice_area && (
                          <span className="practice-area-tag">{normalizePracticeArea(job.practice_area)}</span>
                        )}
                      </td>
                      <td className="seniority-cell">{job.seniority || '-'}</td>
                      <td className="location-cell">{job.location || '-'}</td>
                      <td className="work-type-cell">
                        {job.work_type && (
                          <span
                            className="work-type-tag"
                            style={{ 
                              backgroundColor: getWorkTypeColor(job.work_type),
                              color: job.work_type === 'Unknown' ? '#4b5563' : 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {job.work_type}
                          </span>
                        )}
                        {!job.work_type && '-'}
                      </td>
                      <td className="post-date-cell">{formatDateTime(job.posted_at || job.created_at)}</td>
                      <td>
                        <span
                          className="status-tag"
                          style={{ backgroundColor: getStatusColor(job.status) }}
                        >
                          {getStatusLabel(job.status)}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          {(job.status === 'ready' || job.status === 'matched') ? (
                            <span
                              className="tooltip-wrap"
                              data-tooltip={matchingJobIds.includes(job.id) ? 'Matching…' : job.status === 'matched' ? 'View Matches' : 'Start AI Matching'}
                            >
                              <button
                                className={`action-btn match-btn ${job.status === 'matched' ? 'match-btn--matched' : 'match-btn--ready'}`}
                                onClick={(e) => handleMatchClick(job.id, e)}
                                aria-label={job.status === 'matched' ? 'View Matches' : 'Start AI Matching'}
                                disabled={matchingJobIds.includes(job.id)}
                              >
                                {matchingJobIds.includes(job.id) ? (
                                  <span className="matching-spinner" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="9"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeOpacity="0.25"
                                      />
                                      <path
                                        d="M21 12a9 9 0 0 0-9-9"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </span>
                                ) : job.status === 'matched' ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="currentColor"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor"/>
                                  </svg>
                                )}
                              </button>
                            </span>
                          ) : job.status === 'failed' ? (
                            <span className="tooltip-wrap" data-tooltip={retryingJobId === job.id ? 'Retrying…' : 'Retry Processing'}>
                              <button
                                className="action-btn retry-btn"
                                onClick={(e) => handleRetryJob(job.id, e)}
                                aria-label="Retry Processing"
                                disabled={retryingJobId === job.id}
                              >
                                {retryingJobId === job.id ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="spin">
                                    <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6Z" fill="currentColor"/>
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                                  </svg>
                                )}
                              </button>
                            </span>
                          ) : job.status === 'processing' ? (
                            <span className="tooltip-wrap" data-tooltip={resettingJobId === job.id ? 'Resetting…' : 'Reset to New'}>
                              <button
                                className="action-btn reset-btn"
                                onClick={(e) => handleResetJob(job.id, e)}
                                aria-label="Reset to New"
                                disabled={resettingJobId === job.id}
                              >
                                {resettingJobId === job.id ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="spin">
                                    <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6Z" fill="currentColor"/>
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                                  </svg>
                                )}
                              </button>
                            </span>
                          ) : (
                            <span className="tooltip-wrap" data-tooltip="Enable Job Intelligence">
                              <button
                                className="action-btn"
                                onClick={(e) => handleEnableJobIntelligence(job.id, e)}
                                aria-label="Enable Job Intelligence"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" fill="currentColor"/>
                                </svg>
                              </button>
                            </span>
                          )}
                          {job.status !== 'processing' && (
                            <span className="tooltip-wrap" data-tooltip="Refresh Job">
                              <button
                                className="action-btn"
                                onClick={(e) => { e.stopPropagation(); handleJobAction(job.id, 'refresh'); }}
                                aria-label="Refresh Job"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                                </svg>
                              </button>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <span className="pagination-info">
                Showing {jobs.length > 0 ? (currentPage - 1) * 10 + 1 : 0}-{Math.min(currentPage * 10, stats.total_jobs)} of {stats.total_jobs} jobs
              </span>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ←
                </button>
                {(() => {
                  const getPageNumbers = (current, total) => {
                    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
                    const rangeStart = Math.max(2, current - 2);
                    const rangeEnd = Math.min(total - 1, current + 2);
                    const pages = [1];
                    if (rangeStart > 2) pages.push('...');
                    for (let p = rangeStart; p <= rangeEnd; p++) pages.push(p);
                    if (rangeEnd < total - 1) pages.push('...');
                    pages.push(total);
                    return pages;
                  };
                  return getPageNumbers(currentPage, totalPages).map((page, idx) =>
                    page === '...'
                      ? <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                      : <button
                          key={page}
                          className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                  );
                })()}
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Job Details Modal */}
      {showModal && selectedJobId && (
        <JobDetailsModal
          jobId={selectedJobId}
          onClose={handleCloseModal}
        />
      )}

      {syncing && (
        <div className="custom-search-banner" role="status" aria-live="polite">
          <span className="custom-search-banner__spinner" />
          <span className="custom-search-banner__text">
            Syncing jobs…
            <span className="custom-search-banner__hint">We will notify you when done.</span>
          </span>
        </div>
      )}

      {/* Zero-matches modal */}
      {zeroMatchesModal && (() => {
        const { code, detail, jobTitle } = zeroMatchesModal;
        const tp = detail?.total_from_pearch ?? 0;
        const sc = detail?.skipped_country ?? 0;
        const se = detail?.skipped_excluded ?? 0;

        const configs = {
          NO_PEARCH_RESULTS: {
            iconColor: '#fef3c7',
            iconStroke: '#d97706',
            tag: 'No search results',
            tagClass: 'zero-matches-tag--warn',
            headline: 'No candidates found in Pearch',
            body: 'Pearch returned zero candidates for this job\'s search query. The role requirements may be too specific, or there may not be matching profiles in the database yet.',
            suggestions: [
              'Broaden the job requirements or practice area',
              'Check that the job description is complete and well-formed',
              'Try re-running matching — Pearch\'s index refreshes regularly',
            ],
          },
          ALL_COUNTRY_FILTERED: {
            iconColor: '#ede9fe',
            iconStroke: '#7c3aed',
            tag: 'Location mismatch',
            tagClass: 'zero-matches-tag--purple',
            headline: `${tp} candidate${tp !== 1 ? 's' : ''} found — all outside supported regions`,
            body: `Pearch returned ${tp} candidate${tp !== 1 ? 's' : ''}, but ${sc === 1 ? 'it was' : `all ${sc} were`} filtered out because ${sc === 1 ? 'their' : 'their'} location is outside the supported country list (Latin America + Philippines + Portugal).`,
            suggestions: [
              'The job may be attracting candidates from unsupported regions',
              'Consider whether the role description implies a specific non-supported geography',
              'No action needed if this is expected — matching will improve as the database grows',
            ],
          },
          ALL_CLIENT_EXCLUDED: {
            iconColor: '#fee2e2',
            iconStroke: '#dc2626',
            tag: 'Client conflict',
            tagClass: 'zero-matches-tag--red',
            headline: `${tp} candidate${tp !== 1 ? 's' : ''} found — all excluded (client conflict)`,
            body: `Pearch returned ${tp} candidate${tp !== 1 ? 's' : ''}, but ${se === 1 ? 'the candidate is' : `all ${se} are`} currently employed at an existing LegalSoft client firm and cannot be approached for outreach.`,
            suggestions: [
              'This is an automatic conflict-of-interest protection',
              'The matching result may improve if there is turnover at those firms',
              'Consider whether the job targets a niche where many candidates work at client firms',
            ],
          },
          MIXED_FILTERED: {
            iconColor: '#fef3c7',
            iconStroke: '#d97706',
            tag: 'All candidates filtered',
            tagClass: 'zero-matches-tag--warn',
            headline: `${tp} candidate${tp !== 1 ? 's' : ''} found — all removed by filters`,
            body: `Pearch returned ${tp} candidate${tp !== 1 ? 's' : ''}, but none passed our post-search filters: ${sc} removed due to unsupported location${sc !== 1 ? 's' : ''}, ${se} removed due to client conflict${se !== 1 ? 's' : ''}.`,
            suggestions: [
              'Some candidates were in unsupported regions, others work at client firms',
              'Try broadening the query to reach a wider pool',
              'No action needed for client conflicts — those are automatic exclusions',
            ],
          },
        };

        const cfg = configs[code] || configs.NO_PEARCH_RESULTS;

        return (
          <div
            className="zero-matches-overlay"
            onClick={() => setZeroMatchesModal(null)}
            role="presentation"
          >
            <div
              className="zero-matches-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="zero-matches-title"
            >
              <div className="zero-matches-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill={cfg.iconColor} />
                  <path d="M12 8v4M12 16h.01" stroke={cfg.iconStroke} strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <span className={`zero-matches-tag ${cfg.tagClass}`}>{cfg.tag}</span>
              <h2 id="zero-matches-title" className="zero-matches-title">{cfg.headline}</h2>
              <p className="zero-matches-job"><strong>{jobTitle}</strong></p>
              <p className="zero-matches-body">{cfg.body}</p>
              <div className="zero-matches-suggestions">
                <p className="zero-matches-suggestions-label">What you can do:</p>
                <ul>
                  {cfg.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <button
                type="button"
                className="zero-matches-btn"
                onClick={() => setZeroMatchesModal(null)}
              >
                Got it
              </button>
            </div>
          </div>
        );
      })()}

      {/* Sync completion notification */}
      {syncNotification && (
        <div className="scrape-notification" role="status" aria-live="polite">
          <div className="scrape-notification-icon">
            {syncNotification.timedOut ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
              </svg>
            )}
          </div>
          <div className="scrape-notification-body">
            {syncNotification.timedOut ? (
              <>
                <p className="scrape-notification-title">Sync timed out</p>
                <p className="scrape-notification-line">The sync ran for over 20 minutes and was stopped. Any jobs collected up to that point were saved.</p>
              </>
            ) : (
              <>
                <p className="scrape-notification-title">Ingestion complete</p>
                <p className="scrape-notification-line">
                  <span className="scrape-notification-count">{syncNotification.newJobs}</span> new jobs added
                </p>
                <p className="scrape-notification-line">
                  <span className="scrape-notification-count">{syncNotification.updatedJobs}</span> jobs updated
                </p>
              </>
            )}
          </div>
          <button
            className="scrape-notification-close"
            onClick={() => setSyncNotification(null)}
            aria-label="Dismiss notification"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default Romeo;
